import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  DateRangePicker,
  Pagination,
  Select,
  Table,
  Tag,
  Text,
  Tooltip,
  toISODate,
} from "@app/ui";
import { api, ApiError } from "../api/client.js";
import { useT } from "../i18n.js";
import type { PublicUser } from "../types.js";

interface AuditRow {
  seq: number;
  action: string;
  outcome: string;
  actorId: string | null;
  ip: string | null;
  createdAt: string;
}

// Sentinel actor-filter value for system-originated (null actor) events. Real
// actor ids are UUIDs, so this never collides with one.
const SYSTEM = " system";
const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

export function AuditPage() {
  const t = useT();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [actors, setActors] = useState<Map<string, PublicUser>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [range, setRange] = useState<[string, string]>(["", ""]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const offsetISO = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return toISODate(d);
  };

  useEffect(() => {
    void (async () => {
      try {
        const res = await api<{ items: AuditRow[] }>("/admin/audit?limit=200");
        setRows(res.items);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : t("error.loadFailed"));
      }
    })();
    // Resolve actor ids to names/roles/emails. Best-effort: the viewer may lack
    // user:read (then actors stay unresolved and we show a short id).
    void (async () => {
      try {
        const users = await api<PublicUser[]>("/admin/users?limit=100");
        setActors(new Map(users.map((u) => [u.id, u])));
      } catch {
        /* no user:read — leave actors unresolved */
      }
    })();
  }, []);

  const actionOptions = useMemo(() => {
    const distinct = Array.from(new Set(rows.map((r) => r.action))).sort();
    return [{ label: t("audit.allActions"), value: "" }, ...distinct.map((a) => ({ label: a, value: a }))];
  }, [rows, t]);

  const actorName = (id: string) => {
    const u = actors.get(id);
    return u ? (u.displayName ?? u.email.split("@")[0] ?? u.email) : `${id.slice(0, 8)}…`;
  };

  // Filter options are built from the actors that actually appear in the loaded
  // events (not the whole user directory), so the dropdown stays relevant.
  const actorOptions = useMemo(() => {
    const ids = Array.from(new Set(rows.map((r) => r.actorId)));
    const people = ids
      .filter((id): id is string => id != null)
      .map((id) => ({ label: actorName(id), value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const opts = [{ label: t("audit.allActors"), value: "" }, ...people];
    if (ids.includes(null)) opts.push({ label: t("audit.system"), value: SYSTEM });
    return opts;
  }, [rows, actors, t]);

  const filtered = rows.filter((r) => {
    if (action && r.action !== action) return false;
    if (actor) {
      if (actor === SYSTEM ? r.actorId !== null : r.actorId !== actor) return false;
    }
    if (range[0] || range[1]) {
      const d = toISODate(new Date(r.createdAt));
      if (range[0] && d < range[0]) return false;
      if (range[1] && d > range[1]) return false;
    }
    return true;
  });
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Compact single-line actor cell: name (+ primary role tag) on one row, with
  // the full email and every role in a tooltip — keeps rows short and narrow.
  const renderActor = (r: AuditRow) => {
    if (!r.actorId)
      return (
        <Text secondary small>
          {t("audit.system")}
        </Text>
      );
    const u = actors.get(r.actorId);
    if (!u)
      return (
        <Text secondary small>
          {r.actorId.slice(0, 8)}…
        </Text>
      );
    const name = u.displayName ?? u.email.split("@")[0];
    const primaryRole = u.roles[0];
    const tip = u.roles.length ? `${u.email} · ${u.roles.join(", ")}` : u.email;
    return (
      <Tooltip title={tip}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%" }}>
          <Text small ellipsis style={{ maxWidth: 150 }}>
            {name}
          </Text>
          {primaryRole && <Tag>{primaryRole}</Tag>}
        </span>
      </Tooltip>
    );
  };

  return (
    <Card title={t("audit.title")}>
      {error && <Alert type="error">{error}</Alert>}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: "var(--az-sp-3)" }}>
        <div style={{ minWidth: 220 }}>
          <Select
            showSearch
            options={actionOptions}
            value={action}
            onChange={(v) => {
              setAction(v);
              setPage(1);
            }}
            placeholder={t("audit.filterByAction")}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <Select
            showSearch
            options={actorOptions}
            value={actor}
            onChange={(v) => {
              setActor(v);
              setPage(1);
            }}
            placeholder={t("audit.filterByActor")}
          />
        </div>
        <DateRangePicker
          value={range}
          onChange={(r) => {
            setRange(r);
            setPage(1);
          }}
          allowClear
          presets={[
            { label: t("audit.today"), value: [toISODate(new Date()), toISODate(new Date())] },
            { label: t("audit.last7"), value: [offsetISO(-6), offsetISO(0)] },
            { label: t("audit.last30"), value: [offsetISO(-29), offsetISO(0)] },
          ]}
        />
      </div>
      <Table
        rowKey={(r) => String(r.seq)}
        empty={t("audit.empty")}
        columns={[
          { key: "seq", title: "#", numeric: true, width: 64, render: (r) => r.seq },
          {
            key: "createdAt",
            title: t("audit.time"),
            width: 168,
            render: (r) => <Text small>{new Date(r.createdAt).toLocaleString()}</Text>,
          },
          {
            key: "action",
            title: t("audit.action"),
            render: (r) => <Text small>{r.action}</Text>,
          },
          {
            key: "outcome",
            title: t("audit.outcome"),
            width: 104,
            render: (r) =>
              r.outcome === "failure" ? (
                <Tag color="error">{t("audit.failure")}</Tag>
              ) : (
                <Tag color="success">{t("audit.success")}</Tag>
              ),
          },
          {
            key: "actorId",
            title: t("audit.actor"),
            width: 200,
            render: renderActor,
          },
          {
            key: "ip",
            title: t("common.ip"),
            width: 128,
            render: (r) => <Text small>{r.ip ?? "—"}</Text>,
          },
        ]}
        data={pageRows}
      />
      <div style={{ marginTop: "var(--az-sp-3)" }}>
        <Pagination
          total={filtered.length}
          pageSize={pageSize}
          current={page}
          onChange={setPage}
          showSizeChanger
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
    </Card>
  );
}
